import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import MarketingNav from "../../components/MarketingNav";
import Footer from "../../components/Footer";
import SEO from "../../components/SEO";
import {
  Mail, Building2, User, Briefcase, Send, Loader2, Check,
  Shield, Zap, ArrowRight,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export default function Contact() {
  const [form, setForm] = useState({
    name: "", email: "", company: "", role: "",
    topic: "Sales", message: "",
  });
  const [state, setState] = useState("idle"); // idle | submitting | ok | error
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setState("submitting");
    setError("");
    try {
      const r = await axios.post(`${API_BASE}/api/v1/contact`, { ...form, source: "/contact" });
      if (r.data?.received) setState("ok");
      else throw new Error("Unexpected response");
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(Array.isArray(d) ? "Please check the form — some fields need attention." : (d || "Could not submit. Try again in a moment."));
      setState("error");
    }
  };

  return (
    <div>
      <SEO
        path="/contact"
        title="Contact · Sentry by Lunar Occultation"
        description="Talk to the team behind Sentry. Book a demo, discuss a partnership, report a security issue, or ask the question that doesn't fit anywhere else. We reply within one business day."
        keywords="contact Lunar Occultation, Sentry demo, AI governance demo, book a demo, Lunar Occultation partnership"
      />
      <MarketingNav />

      <section className="relative overflow-hidden bg-cream">
        <div className="absolute inset-0 hero-grid pointer-events-none" />
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] hero-sun rounded-full pointer-events-none" />

        <div className="container-x relative pt-14 pb-20">
          <div className="grid lg:grid-cols-5 gap-10">
            {/* Left: pitch */}
            <div className="lg:col-span-2">
              <span className="chip chip-orange mb-5"><Mail size={12} /> We'd love to hear from you</span>
              <h1 className="font-display text-4xl md:text-5xl font-bold text-navy leading-tight">
                Let's talk about <span className="text-orange">your AI deploy</span>.
              </h1>
              <p className="ink mt-5 text-base md:text-lg max-w-md">
                A human on our team reads every message and replies within one business day.
                Whether you want a demo, a partnership conversation, or you've found a bug — this is the fastest path to us.
              </p>

              <ul className="mt-8 space-y-4 text-sm ink">
                <li className="flex gap-3">
                  <div className="w-9 h-9 shrink-0 rounded-lg bg-[rgba(255,107,53,0.12)] flex items-center justify-center">
                    <Zap size={16} className="text-orange" />
                  </div>
                  <div>
                    <div className="font-semibold text-navy">Sales / demos</div>
                    <div>30-minute walkthrough tailored to your compliance stack (EU AI Act, NIST, SOC 2, HIPAA, FedRAMP).</div>
                  </div>
                </li>
                <li className="flex gap-3">
                  <div className="w-9 h-9 shrink-0 rounded-lg bg-[rgba(255,107,53,0.12)] flex items-center justify-center">
                    <Shield size={16} className="text-orange" />
                  </div>
                  <div>
                    <div className="font-semibold text-navy">Security disclosures</div>
                    <div>We follow a 90-day coordinated-disclosure window. Report via the form and we'll acknowledge within 24 hours.</div>
                  </div>
                </li>
                <li className="flex gap-3">
                  <div className="w-9 h-9 shrink-0 rounded-lg bg-[rgba(255,107,53,0.12)] flex items-center justify-center">
                    <Building2 size={16} className="text-orange" />
                  </div>
                  <div>
                    <div className="font-semibold text-navy">Design partners</div>
                    <div>We're accepting design partners for all our tiers.</div>
                  </div>
                </li>
              </ul>

              <div className="mt-10 pt-6 border-t border-[rgba(10,14,26,0.08)] text-sm ink">
                Prefer the shortcut? Browse the <Link to="/docs" className="text-orange font-semibold hover:underline">docs</Link>, read the <Link to="/blog" className="text-orange font-semibold hover:underline">blog</Link>, or
                <Link to="/register" className="text-orange font-semibold hover:underline"> start a free trial</Link>.
              </div>
            </div>

            {/* Right: form */}
            <div className="lg:col-span-3">
              <div className="rounded-3xl bg-white border border-[rgba(10,14,26,0.08)] p-8 md:p-10 shadow-[0_30px_60px_-40px_rgba(10,14,26,0.25)]">
                {state === "ok" ? (
                  <Success onReset={() => { setState("idle"); setForm({ name:"", email:"", company:"", role:"", topic:"Sales", message:"" }); }} />
                ) : (
                  <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
                    <div className="eyebrow text-orange" style={{ fontSize: 10 }}>SEND US A NOTE</div>
                    <h2 className="font-display text-2xl font-bold text-navy -mt-2">What's on your mind?</h2>

                    <div className="grid md:grid-cols-2 gap-4">
                      <Input testid="contact-name"    icon={User}       label="Your name"      required value={form.name}    onChange={update("name")}    placeholder="Jane Devrel" />
                      <Input testid="contact-email"   icon={Mail}       label="Work email"     required type="email" value={form.email}   onChange={update("email")}   placeholder="jane@yourcompany.com" />
                      <Input testid="contact-company" icon={Building2}  label="Company"        value={form.company} onChange={update("company")} placeholder="Acme Corp" />
                      <Input testid="contact-role"    icon={Briefcase}  label="Your role"      value={form.role}    onChange={update("role")}    placeholder="Head of Risk" />
                    </div>

                    <div>
                      <label className="block">
                        <span className="eyebrow ink text-xs">Message</span>
                        <textarea
                          data-testid="contact-message"
                          required
                          minLength={10}
                          maxLength={4000}
                          value={form.message}
                          onChange={update("message")}
                          placeholder="A sentence or two is plenty. The more context you share about your deploy, the sharper our reply."
                          rows={5}
                          className="mt-1.5 w-full px-4 py-3 rounded-xl border border-[rgba(10,14,26,0.12)] bg-white outline-none focus:ring-2 focus:ring-[#FF6B35]/40 text-sm resize-y"
                        />
                      </label>
                      <div className="text-[11px] ink mt-1 flex items-center justify-between">
                        <span>{form.message.length}/4000</span>
                        <span>We never share your details.</span>
                      </div>
                    </div>

                    {state === "error" && (
                      <div data-testid="contact-error" className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">
                        {error}
                      </div>
                    )}

                    <button
                      type="submit"
                      data-testid="contact-submit"
                      disabled={state === "submitting"}
                      className="btn-primary w-full justify-center disabled:opacity-60"
                    >
                      {state === "submitting" ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <>Send message <Send size={14} /></>}
                    </button>

                    <div className="text-[11px] ink text-center">
                      By submitting you agree to Lunar Occultation's Privacy Notice.
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Input({ testid, icon: Icon, label, required, type = "text", value, onChange, placeholder }) {
  return (
    <label className="block">
      <span className="eyebrow ink text-xs">{label}{required && <span className="text-[#FF6B35]"> *</span>}</span>
      <div className="relative mt-1.5">
        <Icon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" />
        <input
          data-testid={testid}
          type={type}
          required={required}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-[rgba(10,14,26,0.12)] bg-white outline-none focus:ring-2 focus:ring-[#FF6B35]/40 text-sm"
        />
      </div>
    </label>
  );
}

function Success({ onReset }) {
  return (
    <div data-testid="contact-success" className="text-center py-6">
      <div className="w-14 h-14 rounded-full bg-[rgba(34,197,94,0.1)] flex items-center justify-center mx-auto mb-5">
        <Check size={28} className="text-[#22c55e]" />
      </div>
      <h3 className="font-display text-2xl font-bold text-navy">Message received.</h3>
      <p className="ink mt-2 max-w-sm mx-auto text-sm">
        Thanks — a human will reply within one business day. Check your inbox for a confirmation (and maybe a reading list).
      </p>
      <div className="mt-6 flex items-center justify-center gap-3">
        <Link to="/blog" className="btn-ghost text-sm">Read the blog <ArrowRight size={13} /></Link>
        <button type="button" onClick={onReset} data-testid="contact-send-another" className="text-sm text-[#FF6B35] font-semibold hover:underline">Send another</button>
      </div>
    </div>
  );
}
